/** About us and Contact us. The facts come from the app itself: the clouds
 *  the administrator has on, the refresh schedule, the site's name and the
 *  contact address from the profile (or Admin > Website > Site details). */
import { useState } from 'react'
import { useSettings, useSite, VENDOR_LABEL } from '../settings'
import { api } from '../api'
import { useAsync } from './useAsync'

type Go = (v: 'calculator' | 'compare' | 'fit' | 'chat' | 'news' | 'data' | 'privacy' | 'terms' | 'about' | 'contact' | 'neutrality') => void

export function useContactEmail(): string {
  const { site } = useSettings()
  const profile = useSite()
  return site?.contact_email || profile.contact_email || ''
}

export function AboutPage({ go, chat }: { go: Go; chat: boolean }) {
  const { vendors, site } = useSettings()
  const profile = useSite()
  const fresh = useAsync(() => api.freshness(), [])
  const clouds = vendors.map((p) => VENDOR_LABEL[p])
  const list = clouds.length > 1 ? `${clouds.slice(0, -1).join(', ')} and ${clouds[clouds.length - 1]}` : clouds[0] ?? ''
  const every = fresh.data?.refresh_interval_hours ? `every ${fresh.data.refresh_interval_hours} hours` : 'several times a day'
  const prices = (fresh.data?.providers ?? []).reduce((n, p) => n + p.records, 0)
  return (
    <article className="legal panel company">
      <h2>About {profile.name}</h2>
      <p className="lede">
        {profile.name} answers one question for people who run language models: what does it cost, and where is it
        cheapest to run?
      </p>

      <h3>What it does</h3>
      <p>
        It compares every way of hosting a model — paying per token on a managed service, renting GPU instances
        and running it yourself, or a dedicated AI cluster — across {list}, in the region you choose. Every figure
        comes from the clouds' own public price lists{prices ? <>, {prices.toLocaleString()} prices at the last count</> : null},
        collected automatically {every}.
      </p>
      <ul>
        <li><b>Calculator</b>: the monthly cost of your workload under each hosting mode, with the break-even where
          one overtakes another.</li>
        <li><b>Compare</b>: one model, every cloud, side by side.</li>
        <li><b>Fit</b>: which GPU instance or cluster shape can actually hold a model.</li>
        {chat ? <li><b>Ask</b>: plain-language questions, answered only from the price data.</li> : null}
        <li><b>Trending</b>: what changed since the last collection — price moves, new models, retirements and what
          the clouds announced — and a weekly newsletter.</li>
      </ul>

      <h3>How we keep it honest</h3>
      <ul>
        <li>No number is ever typed in or quoted from memory. Prices are read from the vendors' published lists,
          and every calculation runs in one place, with a test.</li>
        <li>List prices only. No negotiated discounts, commitments or free tiers, so what you see is what the vendor
          publishes; the date each price was collected is on the <button type="button" className="linklike" onClick={() => go('data')}>Data page</button>.</li>
        <li>Where a figure rests on an assumption — throughput, GPU busy time, bytes per token — the page says so
          next to the number and lets you change it.</li>
        <li>A collection that fails never replaces the last good one, and a withdrawal is reported only once two
          collections in a row confirm it.</li>
      </ul>
      <p>No vendor pays for, reviews or approves anything here: see the{' '}
        <button type="button" className="linklike" onClick={() => go('neutrality')}>neutrality policy</button>.</p>

      <h3>Who it is for</h3>
      <p>
        Teams choosing where to run a model, finance and FinOps people who own the bill, and anyone who has to
        put a number in front of a customer before a decision is made.
      </p>

      {site?.operator ? (
        <>
          <h3>Who runs it</h3>
          <p>{profile.name} is run by {site.operator}{site.jurisdiction ? `, ${site.jurisdiction}` : ''}.</p>
        </>
      ) : null}

      <p className="small">
        Questions or corrections: <button type="button" className="linklike" onClick={() => go('contact')}>contact us</button>.
      </p>
    </article>
  )
}

/** The neutrality policy (2026-10-17, the owner's call before promoting the site): what the site promises about
 *  vendors, in words the code keeps. Change it when any of these stops being true. */
export function NeutralityPage({ go }: { go: Go }) {
  const { vendors } = useSettings()
  const profile = useSite()
  const n = vendors.length
  return (
    <article className="legal panel company">
      <h2>Neutrality</h2>
      <p className="lede">
        {profile.name} is an index of public list prices for running language models. It favours no vendor, and no
        vendor pays for, reviews or approves anything on it.
      </p>

      <h3>Where the numbers come from</h3>
      <ul>
        <li>Only the list prices each vendor publishes on its own pages and price feeds, for all {n} vendors alike.
          No negotiated discounts, commitments or credits.</li>
        <li>Every price keeps the vendor's own figure and unit, the page it was read from and when it was read; the{' '}
          <button type="button" className="linklike" onClick={() => go('data')}>Data page</button> lists every source.</li>
        <li>Every calculation runs in one place, with the same formula for every vendor, and has a test.</li>
      </ul>

      <h3>How vendors are shown</h3>
      <ul>
        <li>A ranking is by price and nothing else, cheapest first.</li>
        <li>A list that is not a ranking is in alphabetical order.</li>
        <li>The starting options are the cheapest for the starting model, not a vendor chosen in advance; the starting
          model is one that many vendors sell.</li>
        <li>No vendor colour, badge, banner or "recommended" mark. Where a vendor does not sell a model, or not in the
          place you chose, the page says so rather than leaving it out silently.</li>
      </ul>

      <h3>What is not priced yet</h3>
      <p>
        Coverage is not the same for every vendor, and we say where it falls short. Reserved capacity rented by the
        hour is priced for AWS (Bedrock's reserved tier), Azure (provisioned throughput units, at the hourly meter or
        Microsoft's 1-month reservation; the 1-year one is read and named, not yet a choice) and Oracle's dedicated AI
        cluster; Google and Databricks sell it too, and
        the panel says it is not priced here yet. A vendor with one price for every region is shown as such.
      </p>

      <h3>Money</h3>
      <ul>
        <li>No advertising, sponsorship, paid placement or affiliate links. No vendor pays to be listed, ranked or left out.</li>
        <li>Ask writes its answers with a language model hosted on a cloud. It reads the same price data as every page
          and cannot change a price or a ranking.</li>
      </ul>

      <h3>Corrections</h3>
      <p>
        Anyone, a vendor included, can tell us a price is wrong. A correction is made only from a public source, it
        is applied the same way for every vendor, and the price then shows where it was read from.{' '}
        <button type="button" className="linklike" onClick={() => go('contact')}>Report a wrong price</button>.
      </p>
    </article>
  )
}

const TOPICS: [string, string][] = [
  ['price', 'A price that looks wrong'], ['add', 'A cloud or model to add'], ['missing', 'Something the numbers don\'t capture'],
  ['work', 'Working with us'],
  ['press', 'Press'], ['other', 'Something else'],
]

export function ContactPage({ go }: { go: Go }) {
  const profile = useSite()
  const email = useContactEmail()
  const { site } = useSettings()
  const [form, setForm] = useState({ name: '', email: '', topic: 'other', message: '' })
  const [trap, setTrap] = useState('')
  const [state, setState] = useState<'idle' | 'busy' | 'done'>('idle')
  const [error, setError] = useState('')
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => { setForm({ ...form, [k]: e.target.value }); setError('') }
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Tell us your name'); return }
    if (!form.email.trim()) { setError('Enter your email address, so we can reply'); return }
    if (!form.message.trim()) { setError('Write a message'); return }
    setState('busy'); setError('')
    try { await api.contact({ ...form, website: trap }); setState('done') }
    catch (err) { setError(err instanceof Error ? err.message : String(err)); setState('idle') }
  }
  return (
    <article className="legal panel company">
      <h2>Contact us</h2>
      <p className="lede">
        A price that looks wrong, a cloud or model we should add, a partnership, press — write to us here
        {email ? <> or at <a href={`mailto:${email}`} className="contactmail">{email}</a></> : null}. We answer within a few working days.
      </p>

      {state === 'done' ? (
        <div className="sent" role="status">
          <h3>Thank you, {form.name.trim()}.</h3>
          <p>Your message is with us. We will reply to <b>{form.email.trim()}</b>.</p>
          <button type="button" className="linklike" onClick={() => { setForm({ name: '', email: '', topic: 'other', message: '' }); setState('idle') }}>Send another</button>
        </div>
      ) : (
        <form className="contactform" onSubmit={submit} noValidate>
          <div className="two">
            <label>Your name<input type="text" value={form.name} maxLength={120} autoComplete="name" onChange={set('name')} /></label>
            <label>Your email<input type="email" value={form.email} maxLength={254} autoComplete="email" placeholder="you@company.com" onChange={set('email')} /></label>
          </div>
          <label>What it is about
            <select value={form.topic} onChange={set('topic')}>
              {TOPICS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </label>
          <label>Your message
            <textarea rows={7} value={form.message} maxLength={5000} onChange={set('message')}
              placeholder={form.topic === 'price' ? 'The cloud, the model or instance, the region, and what the vendor\'s page says.' : 'What can we help with?'} />
          </label>
          {/* a field people never see or reach: a script that fills it gives itself away */}
          <input className="trap" type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={trap} onChange={(e) => setTrap(e.target.value)} />
          <div className="row">
            <button className="primary" type="submit" disabled={state === 'busy'}>{state === 'busy' ? 'Sending…' : 'Send message'}</button>
            <span className="muted small">{form.message.length} / 5000</span>
          </div>
          {error ? <p className="err small" role="alert">{error}</p> : null}
          <p className="muted small consent">
            We keep your message and your address to reply to you, and for nothing else. See the{' '}
            <button type="button" className="linklike" onClick={() => go('privacy')}>privacy policy</button>.
          </p>
        </form>
      )}

      <h3>Other ways to keep in touch</h3>
      <ul>
        <li>The <button type="button" className="linklike" onClick={() => go('news')}>Trending</button> page and its
          weekly newsletter, for what changed.</li>
        <li>The newsletter, in the footer of every page.</li>
      </ul>

      {site?.operator ? <p className="small muted">{profile.name} is run by {site.operator}{site.jurisdiction ? `, ${site.jurisdiction}` : ''}.</p> : null}
    </article>
  )
}
